/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Empty, Skeleton } from 'antd';
import { useLocation } from 'react-router-dom';
import { TrackerCustomEvent, useTracker, ViewMemberInfoScene } from 'common-package';
import lookie from 'lookie';
import { useMemoizedFn, useSize } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { DotLoading, List } from 'antd-mobile';
import { throttle } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import OrderStateComp from '@/components/order-state';
import { Calendar, Header, HeaderMemberInfo, MultipleSelect, ScrollView, Toast } from '@/components';
import noDataImg from '@/assets/image/no-data.png';
import service from '@/service';
import { TherapyPlanList } from '@/pages/service/add-service/select-plan/constant';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { selectedCurrentCustomer, setCurrentCustomer } from '@/store/customer';
import {
  ALL_PAGES,
  CURRENT_ORDER_IS_DRAFT,
  DAYJS_FORMAT,
  SELECTED_CUSTOMER_ID,
  JUDGE_AS_VERTICAL_SCREEN_HEIGHT,
} from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { CalendarProps } from '@/components/calendar';
import useNav from '@/hooks/useNav';
import ServiceRecordDrawer, { ServiceRecordDrawerRefAttrs } from '../../service-record/components/ServiceRecordDrawer';
import ScreenFlipTip from '../search-member/screen-flip-tip';
import { MemberDrawer } from './member-drawer';
import IsServicing from './is-servicing';
import MemberPhysiotherapyInfo from './member-physiotherapy-info';
import cx from './index.module.scss';

interface Props {}

interface GetServeOrderListParams {
  pageSize: number;
  cursor?: string;
  dateBegin?: string;
  dateEnd?: string;
  itemTypes?: Massage.MassageItem[];
  reset?: boolean;
}

const MemberDetail: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const sizeRef = useRef(null);
  const screenSize = useSize(sizeRef);
  const [isTipOpen, setIsTipOpen] = useState<boolean>(false);

  const currentCustomer = useSelector(selectedCurrentCustomer);
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState<GetCustomerProfileResponse>(currentCustomer);
  const [loadInfoIng, setLoadInfoIng] = useState<boolean>(true);
  const [isDrawerShow, setIsDrawerShow] = useState(false);
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [serviceLists, setServiceLists] = useState<GetServiceOrderListResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreData, setHasMoreData] = useState(true);
  const nav = useNav();
  const { sendEvent } = useTracker();
  const curServeCustomerId = useRef(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [timeRange, setTimeRange] = useState<CalendarProps['value']>(null);
  const isFirstLoad = useRef(true);
  const [isHasServiceRecords, setIsHasServiceRecords] = useState<boolean>(true);
  const [isShowSkeleton, setIsShowSkeleton] = useState<boolean>(false);
  const serviceRecordDrawerRef = useRef<ServiceRecordDrawerRefAttrs>(null);
  const [selectedItemTypes, setSelectedItemTypes] = useState<Massage.MassageItem[]>([]);

  const location = useLocation();
  const [expand, setExpand] = useState<boolean>(false);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerShow(false);
  }, []);

  const selectDate = useMemo(
    () => (timeRange as [Dayjs, Dayjs])?.map(item => item?.format(DAYJS_FORMAT.DATE) ?? null) || [],
    [timeRange]
  );

  const getServeOrderList = useCallback(
    async (params: GetServeOrderListParams) => {
      const { cursor, dateBegin, dateEnd, pageSize = 18, itemTypes, reset = false } = params || {};
      try {
        setIsLoading(true);
        const { data } = await service.service.getServiceOrderList({
          customerId: curServeCustomerId.current,
          itemTypes,
          pageSize,
          ...(cursor ? { cursor } : {}),
          ...(dateBegin ? { dateBegin } : {}),
          ...(dateEnd ? { dateEnd } : {}),
        });
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
          setIsHasServiceRecords(data.length > 0);
        }
        reset ? setServiceLists(data) : setServiceLists([...serviceLists, ...data]);

        const _hasMoreData = data?.length >= pageSize;
        setHasMoreData(_hasMoreData);
        if (_hasMoreData) {
          scrollViewRef.current?.bsInstance?.finishPullUp();
        }
      } finally {
        setIsShowSkeleton(true);
        setIsLoading(false);
      }
    },
    [serviceLists]
  );

  const getCommonServeOrderList = useCallback(
    (selectDate: string[], selectedItemTypes: Massage.MassageItem[]) => {
      if (selectDate?.filter(Boolean)?.length === 2) {
        getServeOrderList({
          pageSize: 18,
          dateBegin: selectDate[0],
          dateEnd: selectDate[1],
          itemTypes: selectedItemTypes,
          reset: true,
        });
      } else {
        getServeOrderList({
          pageSize: 18,
          itemTypes: selectedItemTypes,
          reset: true,
        });
      }
    },
    [getServeOrderList]
  );

  const handleSelectMassageType = useCallback(
    val => {
      setSelectedItemTypes(val);
      getCommonServeOrderList(selectDate, val);
    },
    [getCommonServeOrderList, selectDate]
  );

  const getServingOrder = useCallback(async () => {
    const { data: servingOrder } = await service.service.getServingOrder(curServeCustomerId.current);
    dispatch(setCurrentServingOrder(servingOrder || ({} as any)));

    if (!servingOrder?.orderId) {
      lookie.remove(CURRENT_ORDER_IS_DRAFT);
    }
  }, [dispatch]);

  // Note: 这里不适用Redux信息，新增会员后会直接跳转到这里，因此必须重新获取会员信息
  const getServiceCustomerInfo = useCallback(async () => {
    setLoadInfoIng(true);
    const customerId = lookie.get(SELECTED_CUSTOMER_ID);
    const { data } = await service.customer.getCustomerProfile(customerId);

    setCurrentCustomerInfo(data);
    dispatch(setCurrentCustomer(data));
    setLoadInfoIng(false);
  }, [dispatch]);

  const loadMore = useMemo(
    () =>
      throttle(async () => {
        const lastCursor = serviceLists[serviceLists?.length - 1]?.orderId;
        if (selectDate?.filter(Boolean)?.length === 2) {
          getServeOrderList({
            pageSize: 6,
            cursor: lastCursor,
            dateBegin: selectDate[0],
            dateEnd: selectDate[1],
            itemTypes: selectedItemTypes,
          });
        } else {
          getServeOrderList({
            pageSize: 6,
            cursor: lastCursor,
            itemTypes: selectedItemTypes,
          });
        }
      }, 600),
    [getServeOrderList, selectDate, selectedItemTypes, serviceLists]
  );

  const onEndService = useCallback(
    async (orderId: string) => {
      getServingOrder();
      getServeOrderList({
        pageSize: 18,
        itemTypes: selectedItemTypes,
        reset: true,
      });

      if (!orderId) return;
      const { data } = await service.service.getServiceOrder(orderId);

      if (!data?.onlyManual && data?.step === 'RoboticTask') {
        serviceRecordDrawerRef.current?.showHomePageDrawer(orderId);
      }
    },
    [getServeOrderList, getServingOrder, selectedItemTypes]
  );

  const formatDateGetList = useCallback(
    value => {
      const formatDate = (value as [Dayjs, Dayjs])?.map(item => item?.format(DAYJS_FORMAT.DATE) ?? null) || [];
      getCommonServeOrderList(formatDate, selectedItemTypes);
    },
    [getCommonServeOrderList, selectedItemTypes]
  );

  const calcTimeRange = useCallback(
    (lastDays: number) => () => {
      const today = dayjs();
      const lastFewsDays = today.subtract(lastDays - 1, 'days');
      setTimeRange([lastFewsDays, today]);
      formatDateGetList([lastFewsDays, today]);
    },
    [formatDateGetList]
  );

  const formatDate = useCallback(date => {
    return dayjs(dayjs(date as Dayjs).format(DAYJS_FORMAT.DATE));
  }, []);

  const isSelectDate = useMemo(() => {
    const today = formatDate(dayjs());
    const recentWeek = formatDate(dayjs().subtract(7 - 1, 'days'));
    const recentMonth = formatDate(dayjs().subtract(30 - 1, 'days'));
    // 判断前后结束时间早于 开始时间的状况
    if (
      (today.isSame(formatDate(timeRange?.[1])) && recentWeek.isSame(formatDate(timeRange?.[0]))) ||
      (today.isSame(formatDate(timeRange?.[0])) && recentWeek.isSame(formatDate(timeRange?.[1])))
    ) {
      return 'week';
    } else if (
      (today.isSame(formatDate(timeRange?.[1])) && recentMonth.isSame(formatDate(timeRange?.[0]))) ||
      (today.isSame(formatDate(timeRange?.[0])) && recentMonth.isSame(formatDate(timeRange?.[1])))
    ) {
      return 'month';
    } else {
      return null;
    }
  }, [formatDate, timeRange]);

  const onScrolling = useMemoizedFn(pos => {
    if (pos.y < 0) {
      // 上滑时候收起AI信息
      setExpand(false);
    }
  });

  useEffect(() => {
    curServeCustomerId.current = lookie.get(SELECTED_CUSTOMER_ID);
    getServingOrder();
    getServiceCustomerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { orderId, isNeedShowDrawerAfterFinish } = location?.state || {};
    if (orderId && isNeedShowDrawerAfterFinish) {
      service.service.getServiceOrder(orderId).then(data => {
        // 人工单不弹评价
        !data.data?.onlyManual && serviceRecordDrawerRef.current?.showHomePageDrawer(orderId);
      });
    }
  }, [location?.state]);

  useEffect(() => {
    // 初始加载时候
    const { selectMassageType, selectDate } = location?.state || {};
    if (selectMassageType?.length > 0 || selectDate?.length > 0) {
      getCommonServeOrderList(selectDate, selectMassageType);
      const convertedDates = selectDate?.map(dateString => dayjs(dateString));
      setTimeRange(convertedDates);
      setSelectedItemTypes(selectMassageType);
    } else {
      getServeOrderList({ pageSize: 18 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location?.state) {
      const { currentIsExpand } = location?.state;
      setExpand(currentIsExpand);
    }
  }, [location?.state, setExpand]);

  return (
    <div className={cx('member-detail')} ref={sizeRef}>
      <Header
        title={
          <HeaderMemberInfo
            name={currentCustomerInfo?.name}
            avatarUrl={currentCustomerInfo?.avatarUrl ?? ''}
            handleClick={() => {
              sendEvent?.(TrackerCustomEvent.ViewMemberInfoClk, { et: 'CLK', c1: ViewMemberInfoScene.MemberDetail });
              setIsDrawerShow(true);
            }}
          />
        }
        backConfig={{
          text: '首页',
          action: () => nav(ALL_PAGES.HOME),
        }}
      />

      <div className={cx('right', { 'no-service-record': !isHasServiceRecords })}>
        <Skeleton
          loading={!isShowSkeleton}
          active
          title={false}
          paragraph={{ rows: 1, width: '100%' }}
          className={cx('skeleton-box-one')}
        >
          <div className={cx('service-box', { notServiceRecord: !isHasServiceRecords })}>
            {currentServingOrder?.orderId ? (
              <IsServicing {...currentServingOrder} onEndService={onEndService} />
            ) : (
              <MemberPhysiotherapyInfo
                expand={expand}
                setExpand={setExpand}
                loading={loadInfoIng}
                currentCustomerInfo={currentCustomerInfo}
                isTipOpen={isTipOpen}
                setIsTipOpen={setIsTipOpen}
                isVerticalScreen={screenSize?.height > JUDGE_AS_VERTICAL_SCREEN_HEIGHT}
              />
            )}
          </div>
        </Skeleton>

        <Skeleton
          loading={!isShowSkeleton}
          active
          title={false}
          paragraph={{ rows: 1, width: '100%' }}
          className={cx('skeleton-box-two')}
        >
          {isHasServiceRecords && (
            <>
              <div className={cx('title', 'service-record')}>
                <MultipleSelect
                  list={TherapyPlanList?.map(item => ({ value: item.key, text: item.name }))}
                  value={selectedItemTypes}
                  onChange={handleSelectMassageType}
                  allSelectText="全选"
                  label="理疗类型"
                  className={cx('select-massage-type-wrapper')}
                  multiple
                />

                <Calendar
                  className={cx('calendar')}
                  mode="range"
                  value={timeRange}
                  onChange={value => {
                    setTimeRange(value);
                    formatDateGetList(value);
                  }}
                  disabledDate={current => current && current > dayjs().endOf('day')}
                />
                <span
                  className={cx('time-range', 'line', { active: isSelectDate === 'week' })}
                  onClick={calcTimeRange(7)}
                >
                  近7天
                </span>
                <span className={cx('time-range', { active: isSelectDate === 'month' })} onClick={calcTimeRange(30)}>
                  近30天
                </span>
              </div>
              <ScrollView
                autoRefresh
                realTime
                onScrolling={onScrolling}
                ref={scrollViewRef}
                className={cx('record-list')}
                onPullingUp={loadMore}
              >
                {serviceLists?.length ? (
                  <div className={cx('list-wrapper')}>
                    <List>
                      <div className={cx('member-list')}>
                        {serviceLists.map(item => (
                          <div
                            className={cx('record-item')}
                            key={item.orderId}
                            onClick={() => {
                              if (item.state === 'Serving') {
                                Toast.show('服务中暂不支持查看');
                              } else {
                                nav(`/user/service-detail?orderId=${item.orderId}`, {
                                  state: { selectMassageType: selectedItemTypes, selectDate, currentIsExpand: expand },
                                  replace: false,
                                });
                              }
                            }}
                          >
                            <div className={cx('header')}>
                              <div className={cx('product-name')}>{item.productName}</div>
                              <div className={cx('info')}>
                                <OrderStateComp state={item.state} />
                              </div>
                            </div>
                            <Divider className={cx('divider')} />
                            <div className={cx('create-time')}>服务时间：{item.createTime}</div>
                          </div>
                        ))}
                      </div>
                    </List>
                    <div className={cx('load-data-info')}>{hasMoreData ? '正在加载更多...' : ''}</div>
                  </div>
                ) : isLoading ? (
                  <div className={cx('loading-wrapper')}>
                    <DotLoading />
                    正在加载数据
                  </div>
                ) : (
                  <Empty
                    className={cx('empty')}
                    image={noDataImg}
                    description={<span className={cx('no-service-record-text')}>暂无服务记录</span>}
                  />
                )}
              </ScrollView>
            </>
          )}
        </Skeleton>
      </div>
      <MemberDrawer open={isDrawerShow} memberInfo={currentCustomerInfo} handleClose={handleDrawerClose} />
      <ServiceRecordDrawer ref={serviceRecordDrawerRef} />
      <ScreenFlipTip open={isTipOpen} setOpen={setIsTipOpen} />
    </div>
  );
};

export default MemberDetail;
